import React from "react";
import { useSearchParams } from "react-router-dom";
import { AlertCircle, CheckCircle2, Clock, RefreshCw, ShieldCheck } from "lucide-react";

import Loader from "../../components/ui/Loader";
import { PublicKycHeader } from "../vkyc/PublicKycLayout";
import { digioApi, parseDigioRecord } from "../../services/digio";
import { startDigioSign } from "../../utils/digio";
import type { DigioSdkCallbackResponse } from "../../types/digio";
import {
  getDigioIdentifier,
  isDigioSigningComplete,
  normalizeDigioRecord,
} from "../remittance-beta/utils/digioStatus";
import { DIGIO_STATUS_MESSAGES } from "../remittance-beta/utils/digioConstants";
import { K2DigioRecord, K2DigioStatus } from "../remittance-beta/types/digio/K2Digio";
import { getCurrentIP, logApiErrorToSentry } from "../remittance-beta/utils";
import EsignCard, { EsignErrorCard } from "./components/EsignCard";
import { ESIGN_CARD_CLASS, esignStyles as styles } from "./styles";

const STATUS_ICON: Record<K2DigioStatus, React.ReactNode> = {
  DO_LATER: <Clock className="h-5 w-5 text-amber-600" />,
  AWAITING_SIGN: <ShieldCheck className="h-5 w-5 text-primary" />,
  TOKEN_EXPIRED: <RefreshCw className="h-5 w-5 text-amber-600" />,
  LINK_EXPIRED: <AlertCircle className="h-5 w-5 text-red-500" />,
  SIGNED: <CheckCircle2 className="h-5 w-5 text-green-600" />,
  LINKED: <CheckCircle2 className="h-5 w-5 text-green-600" />,
};

const STATUS_ICON_BG: Record<K2DigioStatus, string> = {
  DO_LATER: "bg-amber-50",
  AWAITING_SIGN: "bg-[#F5F8FF]",
  TOKEN_EXPIRED: "bg-amber-50",
  LINK_EXPIRED: "bg-red-50",
  SIGNED: "bg-[#F0FCF2]",
  LINKED: "bg-[#F0FCF2]",
};

const ESIGN_STEPS = [
  "Review the A2 form details shown in the window.",
  "Complete Aadhaar-based e-sign to submit your A2 form.",
];

const DIGIO_CANCELLED_MESSAGE =
  "Digio cancelled by the user. Please resume to complete the e-sign.";

const isTokenExpiredError = (message?: string | null) =>
  Boolean(message?.toLowerCase().includes("token") && message?.toLowerCase().includes("expir"));

const isDigioUserCancelled = (response?: DigioSdkCallbackResponse | null) => {
  if (!response?.error_code && !response?.message) {
    return false;
  }

  const errorCode = String(response.error_code ?? "");
  const message = (response.message ?? "").toLowerCase();

  return (
    errorCode === "-1000" ||
    errorCode.toLowerCase().includes("cancel") ||
    message.includes("cancel")
  );
};

const DigioEsignPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const uniqueKey = searchParams.get("id") ?? "";

  const [record, setRecord] = React.useState<K2DigioRecord | null>(null);
  const [initialLoading, setInitialLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [signing, setSigning] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [cancelledByUser, setCancelledByUser] = React.useState(false);
  const [completed, setCompleted] = React.useState(false);
  const autoStartAttemptedRef = React.useRef(false);

  const fetchStatus = React.useCallback(async (): Promise<K2DigioRecord | null> => {
    if (!uniqueKey) {
      setError("Invalid e-sign link. Please use the link sent to your mobile or email.");
      setRecord(null);
      return null;
    }

    try {
      const response = await digioApi.getStatus(uniqueKey);
      const nextRecord = normalizeDigioRecord(parseDigioRecord(response) ?? {});
      setRecord(nextRecord);
      setError(null);
      return nextRecord;
    } catch (err: any) {
      const message = err?.response?.data?.error?.type || "Unable to fetch e-sign status.";
      if (isTokenExpiredError(message)) {
        const expiredRecord = { status: "TOKEN_EXPIRED" } as K2DigioRecord;
        setRecord(expiredRecord);
        setError(null);
        return expiredRecord;
      }
      setRecord(null);
      setError(message);
      logApiErrorToSentry(err, "digioEsignPageGetStatus");
      return null;
    }
  }, [uniqueKey]);

  const refreshStatus = React.useCallback(async () => {
    return fetchStatus();
  }, [fetchStatus]);

  const refreshAccessToken = React.useCallback(async () => {
    if (!uniqueKey) {
      return null;
    }

    setRefreshing(true);
    setError(null);

    try {
      const response = await digioApi.refreshAccessToken(uniqueKey);
      const nextRecord = normalizeDigioRecord(parseDigioRecord(response) ?? {});
      setRecord(nextRecord);
      return nextRecord;
    } catch (err: any) {
      const message = err?.response?.data?.error?.type || "Unable to refresh access token.";
      setError(message);
      logApiErrorToSentry(err, "digioRefreshAccessToken");
      return null;
    } finally {
      setRefreshing(false);
    }
  }, [uniqueKey]);

  const handleSignComplete = React.useCallback(
    async (signResponse: Record<string, unknown>) => {
      if (!uniqueKey) {
        return;
      }

      try {
        const ipAddress = (await getCurrentIP()) || "";
        const response = await digioApi.signComplete(uniqueKey, {
          ip_address: ipAddress,
          digio_esign_response: {
            txn_id: String(signResponse?.txn_id ?? ""),
            digio_doc_id: String(signResponse?.digio_doc_id ?? record?.digioDocId ?? ""),
            message: String(signResponse?.message ?? "Signed Successfully"),
          },
        });
        const nextRecord = normalizeDigioRecord(parseDigioRecord(response) ?? {});
        setRecord(nextRecord);
        setCompleted(true);
        setCancelledByUser(false);
        setError(null);
      } catch (err: any) {
        const message = err?.response?.data?.error?.type || "Unable to complete e-sign.";
        setError(message);
        logApiErrorToSentry(err, "digioSignComplete");
      }
    },
    [record?.digioDocId, uniqueKey]
  );

  const startSigning = React.useCallback(
    async (activeRecord: K2DigioRecord) => {
      const documentId = activeRecord.digioDocId;
      const identifier = getDigioIdentifier(activeRecord);
      const accessToken = activeRecord.digioDocAccessToken ?? undefined;

      if (!documentId || !identifier) {
        setError(
          "Missing Digio signing details. Please request a new link from the review screen."
        );
        return;
      }

      setSigning(true);
      setError(null);
      setCancelledByUser(false);

      try {
        await startDigioSign({
          documentId,
          identifier,
          accessToken,
          onSuccess: async (response) => {
            await handleSignComplete(response);
            setSigning(false);
          },
          onError: (response) => {
            setSigning(false);
            if (isDigioUserCancelled(response)) {
              setCancelledByUser(true);
              setError(null);
              return;
            }
            setCancelledByUser(false);
            setError(response?.message || "Digio signing failed.");
          },
        });
      } catch (err: any) {
        setError(err?.message || "Unable to start Digio signing.");
        setSigning(false);
      }
    },
    [handleSignComplete]
  );

  const handleRefreshAndSign = React.useCallback(async () => {
    const refreshedRecord = await refreshAccessToken();
    if (refreshedRecord?.status === "AWAITING_SIGN") {
      await startSigning(refreshedRecord);
      return;
    }
    if (!error) {
      setError("Unable to refresh signing session. Please try again.");
    }
  }, [error, refreshAccessToken, startSigning]);

  React.useEffect(() => {
    let isActive = true;
    autoStartAttemptedRef.current = false;
    setInitialLoading(true);

    const loadInitialStatus = async () => {
      try {
        await fetchStatus();
      } finally {
        if (isActive) {
          setInitialLoading(false);
        }
      }
    };

    loadInitialStatus();

    return () => {
      isActive = false;
    };
  }, [fetchStatus, uniqueKey]);

  React.useEffect(() => {
    if (initialLoading || completed || autoStartAttemptedRef.current || !record) {
      return;
    }

    if (isDigioSigningComplete(record.status)) {
      setCompleted(true);
      return;
    }

    if (record.status === "TOKEN_EXPIRED" || record.status !== "AWAITING_SIGN") {
      return;
    }

    autoStartAttemptedRef.current = true;
    startSigning(record);
  }, [completed, initialLoading, record, startSigning]);

  const status = completed
    ? isDigioSigningComplete(record?.status)
      ? record?.status
      : "SIGNED"
    : record?.status;
  const statusCopy = status ? DIGIO_STATUS_MESSAGES[status] : null;
  const isActionLoading = signing || refreshing;
  const showSignActions = status === "AWAITING_SIGN" && !completed;
  const showRefreshAction = status === "TOKEN_EXPIRED" && !completed;

  const handleRetry = async () => {
    setInitialLoading(true);
    try {
      await refreshStatus();
    } finally {
      setInitialLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <PublicKycHeader />
      <div className={styles.body}>
        {initialLoading ? (
          <div className={ESIGN_CARD_CLASS}>
            <div className={styles.cardBody}>
              <div className="flex justify-center py-10">
                <Loader />
              </div>
            </div>
          </div>
        ) : null}

        {!initialLoading && !status && error ? (
          <EsignErrorCard error={error} onRetry={handleRetry} />
        ) : null}

        {!initialLoading && status && statusCopy ? (
          <EsignCard
            record={record ?? ({ status } as K2DigioRecord)}
            status={status}
            statusCopy={statusCopy}
            statusIcon={STATUS_ICON[status]}
            statusIconBg={STATUS_ICON_BG[status]}
            steps={ESIGN_STEPS}
            cancelledByUser={cancelledByUser}
            cancelledMessage={DIGIO_CANCELLED_MESSAGE}
            error={error}
            completed={completed}
            showSignActions={showSignActions}
            showRefreshAction={showRefreshAction}
            isActionLoading={isActionLoading}
            signing={signing}
            onStartSigning={() => record && startSigning(record)}
            onRefreshAndSign={handleRefreshAndSign}
            onRefreshStatus={refreshStatus}
          />
        ) : null}
      </div>
    </div>
  );
};

export default DigioEsignPage;
